import React, { useState, useRef, useMemo, useEffect } from 'react'
import { Typeahead, asyncContainer } from 'react-bootstrap-typeahead'
import styled, { keyframes } from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import { useUser } from 'providers/UserProvider'

import { X } from 'phosphor-react'
import { Text } from 'components/v2/Text'
import { Camera, Close, Location } from 'components/v2/Icons'
import { Button } from 'components/v2/Button'
import { Flag } from 'components/v2/Icons/Flag'
import { Row, Column } from 'components/v2/Grid'
import { Checkbox } from 'components/v2/Checkbox'
import { MaskedInput } from 'components/v2/Input'
import { MultiSelect } from 'components/v2/Select'
import { RangeSlider } from 'components/v2/RangeSlider'
import TypeaheadSelect from 'components/v2/MoreFilters/TypeheadSelect'

import { collapse, updateFilter } from 'redux/modules/filters'
import { checkIsMobile } from 'utils/helpers'
import { autoSearch } from 'services/psychologists'
import { useMutation } from '@tanstack/react-query'
import PlacesAutocomplete, { geocodeByAddress, getLatLng } from 'react-places-autocomplete'

const AsyncTypeahead = asyncContainer(Typeahead)

const _AsyncTypeahead = React.forwardRef((props, ref) => {
  return <AsyncTypeahead ref={ref} {...props} />
})

const isMobile = checkIsMobile(993)

const MoreFilters = ({ onSearchPsychologists = () => {} }) => {
  const [hasTypedAnInvalidAddress, setHasTypedAnInvalidAddress] = useState(false)
  const [searchedPsychologistsByName, setSearchedPsychologistsByName] = useState([])

  const nameRef = useRef()
  const dispatch = useDispatch()
  const { shouldHideConsultationValue, isPresencialConsultationEnabled } = useUser()
  const { filters, skills } = useSelector(state => ({
    filters: state.filters,
    skills: state.skills
  }))

  const isOnlineModality = useMemo(() => filters?.consultation === 'online', [filters])

  const onToggleMoreFiltersVisibility = () => dispatch(collapse())

  const onPsychologistChange = ([name]) => dispatch(updateFilter({ nameProfessional: name }))

  const { mutate: onSearchPsychologist, isLoading } = useMutation({
    mutationFn: ({ nameProfessional }) => autoSearch({ nameProfessional }),
    onSuccess: ({ data: { nameProfessional } }) => {
      setSearchedPsychologistsByName(nameProfessional)
    },
    onError: error => {
      console.log(error)
    }
  })

  const onManuallyChangeConsultationPriceRange = (range, value) => {
    const number = Number(value)
    const payload = {
      min: { min_price: number },
      max: { max_price: number }
    }
    dispatch(updateFilter(payload[range]))
  }

  const onValidateConsultationPriceRange = value => {
    if (filters?.min_price > filters?.max_price) {
      return 'O valor mínimo não pode ser maior que o valor máximo'
    }
    if (value < 50 || value > 350) {
      return 'O valor deve ser no mínimo 50 e no máximo 350'
    }
    return false
  }

  const onSelectSkill = skills => {
    if (skills?.length > 3) {
      return
    }
    return dispatch(updateFilter({ skills }))
  }

  const onAddressChange = async address => {
    dispatch(updateFilter({ address }))
  }

  const onConsultationModalityChange = consultation => {
    dispatch(updateFilter({ consultation }))
  }

  const onPsychologistGenderChange = (gender, isSelected) =>
    dispatch(updateFilter({ [gender]: isSelected }))

  const onConsultationTargetAudienceChange = (targetAudience, isSelected) =>
    dispatch(updateFilter({ [targetAudience]: isSelected }))

  const onChangeConsultationPriceRange = ([min_price, max_price]) =>
    dispatch(updateFilter({ min_price, max_price }))

  const onSelectLanguage = language =>
    dispatch(
      updateFilter({
        languages_code: filters?.languages_code?.includes(language)
          ? filters?.languages_code?.filter(it => it !== language)
          : [...filters?.languages_code, language]
      })
    )

  function handleKeyDown(e) {
    const instance = nameRef.current.getInstance()
    e?.keyCode === 8 && filters?.nameProfessional && instance.clear()
  }

  useEffect(() => {
    ;(async () => {
      await geocodeByAddress(filters?.address).then(results =>
        getLatLng(results[0]).then(latLng =>
          dispatch(
            updateFilter({
              lat: latLng.lat,
              long: latLng.lng
            })
          )
        )
      )
    })()
  }, [filters?.address])

  return (
    <BackgroundContainer isOpen={filters?.collapsed}>
      <CollapseContainer isOpen={filters?.collapsed} isMobile={isMobile}>
        <Row
          justifyContent='space-between'
          alignItems='center'
          borderBottom='2px solid'
          borderColor='gray.n250'
          maxHeight='71px'
          width='100%'
          py='22px'
          px={['18px', '98px']}
          mb='30px'
        >
          <Text fontSize='26px'>Filtros</Text>
          <Close size='16px' onClick={onToggleMoreFiltersVisibility} />
        </Row>

        <Column px={['18px', '98px']}>
          {isPresencialConsultationEnabled && (
            <Row mb='15px'>
              <StyledButton
                variant={isOnlineModality ? 'primary-outlined-inactive' : 'primary-outlined-active'}
                onClick={() => onConsultationModalityChange('presencial')}
                type='button'
                height={48}
                width='100%'
                mr='16px'
              >
                <Row justifyContent='center' alignItems='center'>
                  <Location size='13px' color={isOnlineModality ? '#7F8C8D' : '#FF6A58'} />
                  <Text variant='small' ml='9px'>
                    Presencial
                  </Text>
                </Row>
              </StyledButton>
              <StyledButton
                variant={isOnlineModality ? 'primary-outlined-active' : 'primary-outlined-inactive'}
                onClick={() => onConsultationModalityChange('online')}
                type='button'
                height={48}
                width='100%'
              >
                <Row justifyContent='center' alignItems='center'>
                  <Camera size='14px' color={isOnlineModality ? '#FF6A58' : '#7F8C8D'} />
                  <Text variant='small' ml='9px'>
                    Online
                  </Text>
                </Row>
              </StyledButton>
            </Row>
          )}
          <Column mb='15px'>
            <TypeaheadSelect
              title='Procure por especialidade ou transtorno'
              titleColor='#FF7708'
              emptyLabel='Nenhuma sugestão encontrada'
              placeholder='Como Ansiedade, Depressão, TDAH'
              showMoreLabel='Mostrar mais resultados...'
            />
          </Column>
          <Column
            height='52px'
            bg='white'
            py='8px'
            borderRadius='4px'
            border='0.5px solid'
            borderColor='gray.n300'
            alignItems='flex-start'
            mb='30px'
            position='relative'
          >
            <Text fontSize='10px' textAlign='left' color='primary' px='12px'>
              Nome do Psicólogo
            </Text>
            <StyledAsyncTypeahead
              id='professional'
              isLoading={isLoading}
              onSearch={onSearchPsychologist}
              onChange={onPsychologistChange}
              options={searchedPsychologistsByName}
              defaultInputValue={filters?.nameProfessional}
              placeholder='Sabe o nome do seu psicólogo? Digite aqui!'
              searchText='Carregando...'
              emptyLabel='Nenhum psicólogo encontrado.'
              promptText='Digite para buscar..'
              ref={nameRef}
              onKeyDown={handleKeyDown}
            />
            {!!filters?.nameProfessional?.length && (
              <X
                size={20}
                color='#FF7708'
                onClick={() => nameRef.current.getInstance().clear()}
                style={{ right: '0.5rem', top: '33%', position: 'absolute', cursor: 'pointer' }}
              />
            )}
          </Column>
          {!isOnlineModality && (
            <Column mb='30px'>
              <Column
                height='52px'
                bg='white'
                py='8px'
                px='12px'
                borderRadius='4px'
                border='0.5px solid'
                borderColor={hasTypedAnInvalidAddress ? 'red' : 'gray.n300'}
                alignItems='flex-start'
                mb='2px'
              >
                <Text fontSize='10px' textAlign='left' color='primary'>
                  Onde você está?
                </Text>
                <PlacesAutocomplete
                  inputProps={{
                    placeholder: 'CEP, bairro ou cidade',
                    type: 'search',
                    value: filters?.address,
                    onChange: address => {
                      onAddressChange(address)

                      if (hasTypedAnInvalidAddress) {
                        setHasTypedAnInvalidAddress(false)
                      }
                    }
                  }}
                  autocompleteItem={({ formattedSuggestion }) => (
                    <Row justifyContent='flex-start' alignItems='center' flexWrap='wrap'>
                      <Text variant='regular' fontWeight='bold' mr='5px'>
                        {formattedSuggestion.mainText}
                      </Text>
                      <Text variant='tiny'> {formattedSuggestion.secondaryText}</Text>
                    </Row>
                  )}
                  styles={{
                    autocompleteContainer: {
                      display: 'flex',
                      flexDirection: 'column',
                      width: '100%',
                      minWidth: '300px',
                      borderColor: '#BDC3C7',
                      top: 28,
                      left: -12,
                      zIndex: 3
                    },
                    input: {
                      fontSize: '16px',
                      minWidth: '260px',
                      height: 'auto'
                    }
                  }}
                  onError={() => setHasTypedAnInvalidAddress(true)}
                  type='search'
                  placeholder='CEP, bairro ou cidade'
                  googleLogo={false}
                  options={{
                    componentRestrictions: { country: 'br' }
                  }}
                />
              </Column>
              {hasTypedAnInvalidAddress && (
                <Text textAlign='left' color='red' fontSize={['9px', '11px']}>
                  Digite um endereço válido!
                </Text>
              )}
            </Column>
          )}
          <Row borderBottom='2px solid' borderColor='gray.n250' mb='30px' />
          {!shouldHideConsultationValue && (
            <Column>
              <Text variant='regular' textAlign='left' mb='17px'>
                Valor da consulta
              </Text>

              <RangeSlider
                range
                min={50}
                max={350}
                value={[filters?.min_price, filters?.max_price]}
                onChange={onChangeConsultationPriceRange}
                allowCross={false}
                pushable={9}
              />

              <Row my='30px' justifyContent='center'>
                <MaskedInput
                  type='number'
                  variant='primary'
                  onChange={({ target }) =>
                    onManuallyChangeConsultationPriceRange('min', target.value)
                  }
                  mask='no_mask'
                  width='100%'
                  value={filters?.min_price}
                  name='min_price'
                  label='Mínimo*'
                  placeholder='R$50'
                  error={onValidateConsultationPriceRange(filters?.min_price)}
                  mb='0px'
                />
                <Column height='48px' justifyContent='center'>
                  <Row
                    width='9px'
                    borderBottom='2px solid'
                    borderColor='gray.n250'
                    height='2px'
                    mx='12px'
                  ></Row>
                </Column>
                <MaskedInput
                  type='number'
                  variant='primary'
                  onChange={({ target }) =>
                    onManuallyChangeConsultationPriceRange('max', target.value)
                  }
                  mask='no_mask'
                  width='100%'
                  value={filters?.max_price}
                  name='max_price'
                  label='Máximo*'
                  placeholder='R$350'
                  error={onValidateConsultationPriceRange(filters?.max_price)}
                  mb='0px'
                />
              </Row>
              <Row borderBottom='2px solid' borderColor='gray.n250' mb='30px' />
            </Column>
          )}
          <Text variant='regular' textAlign='left' mb='17px'>
            Abordagem
          </Text>
          <Row justifyContent='center' mb='30px'>
            <MultiSelect
              variant='secondary'
              name='skills'
              placeholder='Ver todas'
              options={
                skills?.data.map(skill => {
                  return {
                    label: skill?.title,
                    value: skill?.id
                  }
                }) || []
              }
              value={filters?.skills}
              onChange={event => onSelectSkill(event.target.value)}
            />
          </Row>
          <Row borderBottom='2px solid' borderColor='gray.n250' mb='30px' />
          <Text variant='regular' textAlign='left' mb='17px'>
            Gênero do psicólogo
          </Text>
          <Row mb='30px'>
            <Checkbox
              name='female'
              label='Feminino'
              checked={filters?.female}
              onClick={() => onPsychologistGenderChange('female', !filters?.female)}
            />
            <Checkbox
              name='male'
              label='Masculino'
              checked={filters?.male}
              onClick={() => onPsychologistGenderChange('male', !filters?.male)}
            />
          </Row>
          <Row borderBottom='2px solid' borderColor='gray.n250' mb='30px' />
          <Text variant='regular' textAlign='left' mb='17px'>
            Idiomas e acessibilidade
          </Text>
          <Row width='100%' height='auto' mb='30px'>
            <Column width='50%'>
              <Checkbox
                name='pt-br'
                label='Português'
                checked={filters?.languages_code?.includes('pt-br')}
                icon={<Flag variant='portuguese' />}
                onClick={() => onSelectLanguage('pt-br')}
              />
              <Checkbox
                name='en-us'
                label='Inglês'
                checked={filters?.languages_code?.includes('en-us')}
                icon={<Flag variant='english' />}
                onClick={() => onSelectLanguage('en-us')}
              />
              <Checkbox
                name='es-es'
                label='Espanhol'
                checked={filters?.languages_code?.includes('es-es')}
                icon={<Flag variant='spanish' />}
                onClick={() => onSelectLanguage('es-es')}
              />
              <Checkbox
                name='it-ele'
                label='Italiano'
                checked={filters?.languages_code?.includes('it-ele')}
                icon={<Flag variant='italian' />}
                onClick={() => onSelectLanguage('it-ele')}
              />
            </Column>
            <Column width='50%'>
              <Checkbox
                name='fr-fr'
                label='Francês'
                checked={filters?.languages_code?.includes('fr-fr')}
                icon={<Flag variant='french' />}
                onClick={() => onSelectLanguage('fr-fr')}
              />
              <Checkbox
                name='ja-ja'
                label='Japonês'
                checked={filters?.languages_code?.includes('ja-ja')}
                icon={<Flag variant='japanese' />}
                onClick={() => onSelectLanguage('ja-ja')}
              />
              <Checkbox
                name='zh-cn'
                label='Chinês'
                checked={filters?.languages_code?.includes('zh-cn')}
                icon={<Flag variant='chinese' />}
                onClick={() => onSelectLanguage('zh-cn')}
              />
              <Checkbox
                name='asgn-b'
                label='Libras'
                checked={filters?.languages_code?.includes('asgn-b')}
                icon={<Flag variant='signLanguage' />}
                onClick={() => onSelectLanguage('asgn-b')}
              />
            </Column>
          </Row>
          <Row borderBottom='2px solid' borderColor='gray.n250' mb='30px' />
          <Text variant='regular' textAlign='left' mb='17px'>
            Atendimento
          </Text>
          <Row mb='30px'>
            <Column width='50%'>
              <Checkbox
                name='adult'
                label='Adultos'
                checked={filters?.adult}
                onClick={() => onConsultationTargetAudienceChange('adult', !filters?.adult)}
              />
              <Checkbox
                name='adolescent'
                label='Adolescentes'
                checked={filters?.adolescent}
                onClick={() =>
                  onConsultationTargetAudienceChange('adolescent', !filters?.adolescent)
                }
              />
              <Checkbox
                name='kids'
                label='Crianças'
                checked={filters?.kids}
                onClick={() => onConsultationTargetAudienceChange('kids', !filters?.kids)}
              />
            </Column>
            <Column width='50%'>
              <Checkbox
                name='elderly'
                label='Idosos'
                checked={filters?.elderly}
                onClick={() => onConsultationTargetAudienceChange('elderly', !filters?.elderly)}
              />
              <Checkbox
                name='couple'
                label='Casais'
                checked={filters?.couple}
                onClick={() => onConsultationTargetAudienceChange('couple', !filters?.couple)}
              />
            </Column>
          </Row>
        </Column>
        <Row
          background='white'
          justifyContent='space-between'
          alignItems='center'
          boxShadow='0px 0px 12px #00000012'
          position='sticky'
          bottom={0}
          maxHeight='71px'
          width='100%'
          py='15px'
          px={['18px', '98px']}
        >
          <Button
            type='submit'
            onClick={() => {
              onToggleMoreFiltersVisibility()
              onSearchPsychologists()
            }}
            height={48}
            width='100%'
          >
            <Text variant='small' fontWeight='bold'>
              Aplicar filtros
            </Text>
          </Button>
        </Row>
      </CollapseContainer>
    </BackgroundContainer>
  )
}

export default MoreFilters

const ImageIndex = styled.div`
  z-index: 2;
`

const fadeIn = () => keyframes`
  from {
    max-width: 0;
    min-width: 0;
    opacity: 0;
  }
  to {
    max-width: 534px;
    min-width: 534px;
    opacity: 1;
  }
`
const fadeInMobile = () => keyframes`
  from {
    max-width: 0;
    min-width: 0;
    opacity: 0;
  }
  to {
    max-width: 100%;
    min-width: 100%;
    opacity: 1;
  }
`

const BackgroundContainer = styled.div`
  ${({ isOpen }) => {
    const background = `
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    position:fixed;
    display: flex;
    z-index: 9999;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.4);
    margin:0px;
    padding:0px;
    `

    if (isOpen) {
      return background
    }
  }}
`

const CollapseContainer = styled(Column)`
  overflow-y: auto;
  display: ${({ isOpen }) => (isOpen ? 'flex' : 'none')};
  width: 100%;
  height: 100%;
  position: fixed;
  z-index: 99999;
  background: ${({ theme: { colors } }) => colors.white};
  top: 0;
  right: 0;
  animation: ${({ isMobile }) => (isMobile ? fadeInMobile : fadeIn)} 0.5s ease forwards;
  @media (min-width: 994px) {
    max-width: 534px;
  }
`
const StyledLink = styled.a`
  color: #acacac;
  font-weight: 900;
  text-decoration: underline;
  margin-left: 3px;

  &:hover {
    color: #000;
  }
`

const StyledTypeahead = styled(Typeahead)`
  div {
    height: auto;
  }
  .rbt-input {
    input {
      width: 268px !important;
      font-size: 16px !important;
      padding: 0 12px !important;
      @media (max-width: 768px) {
        width: 510px !important;
      }
      @media (max-width: 425px) {
        width: 320px !important;
      }
      @media (max-width: 375px) {
        width: 280px !important;
      }
      @media (max-width: 320px) {
        width: 225px !important;
      }
    }
  }
  .dropdown-menu {
    margin: 3px 0 0 0 !important;
    width: 320px !important;
  }
`

const StyledAsyncTypeahead = styled(_AsyncTypeahead)`
  div {
    height: auto;
  }
  .rbt-input {
    input {
      width: 295px !important;
      font-size: 16px !important;
      padding: 0 12px !important;
      @media (max-width: 768px) {
        width: 510px !important;
      }
      @media (max-width: 425px) {
        width: 320px !important;
      }
      @media (max-width: 375px) {
        width: 280px !important;
      }
      @media (max-width: 320px) {
        width: 225px !important;
      }
    }
  }
  .dropdown-menu {
    margin: 3px 0 0 0 !important;
  }
`

const StyledButton = styled(Button)`
  :focus {
    outline: none;
  }
`
